import get from "lodash/get";
import React, { useState } from "react";
import Stack from "@mui/material/Stack";
import { makeStyles } from "@material-ui/core";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { getMobMaxWidthPixels } from "./layout/mobile";
import { CurrencyValue } from "./CurrencyValue";
import { AddToTrolleyButton } from "./AddToTrolleyButton";
import { useIsMobile } from "../actions/ui";

/**
 * Default pages:
 *   >=1280px: 4 per row
 *   <1280px : 3 per row
 *   <960px  : 2 per row
 *   <= mob  : 1 per row (with wider side padding)
 *
 * Map page (/customer/map) or prop context="map":
 *   width: clamp(260px, 31%, 360px)
 *   → Tries 3-up, but never <260px (wraps to 2-up/1-up as needed).
 */

// Reusable base for the title/album "pills"
const pillBase = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 13,
  fontWeight: 600,
  border: "1px solid #ddd",
  maxWidth: "100%",
  overflow: "hidden",
  whiteSpace: "normal",
  wordBreak: "break-word",
  backgroundColor: "#fff",
  color: "#000",
};

const useStyles = makeStyles({
  card: {
    cursor: "pointer",
    position: "relative",
    display: "inline-block",
    verticalAlign: "top",
    boxSizing: "border-box",
    padding: "8px",
    width: "25%",
    "@media (max-width:1279.98px)": { width: "33.3333%" },
    "@media (max-width:959.98px)": { width: "50%" },

    // MOBILE: 1 per row — generous horizontal padding
    [`@media only screen and (max-width:${getMobMaxWidthPixels()})`]: {
      width: "100%",
      padding: "12px 16px 12px",
    },

    // visible outline when focused via keyboard (accessibility)
    "&:focus-within .card-inner, &:focus .card-inner": {
      outline: "2px solid rgba(0,0,0,0.25)",
      outlineOffset: "2px",
    },
  },

  // Map page override — clamped width keeps cards from getting skinny
  cardMap: {
    width: "clamp(260px, 31%, 360px)",
    [`@media only screen and (max-width:${getMobMaxWidthPixels()})`]: {
      width: "100%",
      padding: "12px 16px 12px",
    },
  },

  cardInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    background: "#fff",
    borderRadius: "6px 6px 10px 6px", // subtle “book corner”
    overflow: "hidden",
    border: "1px solid #ddd",

    // Multi-shadow for "stacked pages / photo album" look
    boxShadow: `
      0 2px 3px rgba(0,0,0,0.08),
      2px 4px 6px rgba(0,0,0,0.10),
      4px 6px 8px rgba(0,0,0,0.08),
      6px 8px 10px rgba(0,0,0,0.06)
    `,
  },

  mediaBox: {
    position: "relative",
    width: "100%",
    aspectRatio: "3 / 2",
    background: "#fff",
    overflow: "hidden",
  },

  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    border: 0,
  },

  // (kept for future use; not used now)
  imgPortraitTop: { objectPosition: "top" },

  innerLabelWrapper: {
    position: "absolute",
    width: "92%",
    left: 0,
    right: 0,
    margin: "auto",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 2,
    top: 12,
    display: "flex",
    gap: 8,
  },

  photoIsFavoriteWrapper: {
    color: "#fff",
    textAlign: "center",
    borderRadius: 20,
    padding: "4px 8px",
    cursor: "pointer",
    backgroundColor: "rgb(0 0 0 / 40%)",
  },

  photoIsFavorite: { color: "red" },

  galleryPriceWrapper: {
    minWidth: 90,
    color: "#fff",
    textAlign: "center",
    borderRadius: 20,
    backgroundColor: "rgb(0 0 0 / 40%)",
    padding: "2px 8px",
    "& p": { margin: 0, fontWeight: 600 },
  },

  shoppingCartButtonWrapper: {
    color: "#fff",
    textAlign: "center",
    borderRadius: 20,
    padding: "4px 8px",
    cursor: "pointer",
    backgroundColor: "rgb(0 0 0 / 40%)",
  },

  // Title row BELOW the image
  titleRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 12px 12px",
    flexWrap: "wrap",
    [`@media only screen and (max-width:${getMobMaxWidthPixels()})`]: {
      flexDirection: "column",
      gap: 6,
      alignItems: "stretch",
      padding: "10px 10px 12px",
    },
  },

  titlePill: {
    ...pillBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },

  albumPill: {
    ...pillBase,
    textAlign: "center",
  },

  pillText: { margin: 0, padding: 0 },

  // Title styles (split line)
  dateLine: {
    margin: 0,
    padding: 0,
    fontSize: 12,
    lineHeight: 1.15,
    color: "#666",
    fontWeight: 600,
  },
  titleMain: {
    margin: 0,
    padding: 0,
    fontSize: 14,
    lineHeight: 1.2,
    color: "#000",
    fontWeight: 600,
    textAlign: "center",
    wordBreak: "break-word",
  },

  card_with_no_image: {
    background: "#fff",
    width: "100%",
    aspectRatio: "3 / 2",
    position: "relative",
  },

  coming_soon_text: {
    position: "absolute",
    inset: 0,
    margin: "auto",
    height: "fit-content",
    width: "100%",
    textAlign: "center",
    fontSize: 16,
    color: "#333",
  },

  photo_art_card: {
    width: "100%",
    height: "100%",
    margin: "auto",
    display: "flex",
    aspectRatio: "3/2",
    overflow: "hidden",
    textAlign: "center",
    justifyContent: "center",
    "& $img": {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "cover",
      border: "none",
    },
  },

  eventsCardWrapper: {
    width: "100%",

    // Default image handling inside events area
    "& $img": {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "cover",
      border: 0,
    },

    // EVENT LOGOS (cardType === 'gallery-event'): contain + top padding
    "&.cardtype-gallery-event $img": { objectFit: "contain" },
    "&.cardtype-gallery-event $mediaBox": { paddingTop: 12 },
  },

  defaultSrc: {
    width: "100%",
    border: "none",
    height: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
});

// --- helper: split a title like "7th May '25. 14:00 oversize"
function splitDateTimeAndName(raw) {
  if (!raw) return { dateTime: "", rest: "" };
  const s = String(raw).trim();
  const timeRe = /\b\d{1,2}:\d{2}\s*(?:[ap]\.?m\.?)?/i;
  const m = s.match(timeRe);
  if (!m) return { dateTime: "", rest: s };
  const end = m.index + m[0].length;
  let dateTime = s.slice(0, end).trim();
  let rest = s.slice(end).trim();
  rest = rest.replace(/^[-–—.,;:]+/, "").trim();
  return { dateTime, rest };
}

// PORTRAIT OFFSET: given natural H/W and a 3:2 card, find the y% so
// the card's **bottom** aligns with the portrait image's midpoint.
function computePortraitYPercent(naturalHeight, naturalWidth) {
  if (!naturalHeight || !naturalWidth) return 0;
  if (naturalHeight <= naturalWidth) return 50; // landscape → center (unused branch)
  const r = naturalHeight / naturalWidth; // image H/W
  const k = 2 / 3; // card H/W (3:2 aspect → 0.666...)
  // P = (r/2 - k) / (r - k) mapped to percent, clamped [0, 50]
  let p = (r / 2 - k) / (r - k);
  if (!isFinite(p)) p = 0;
  p = Math.max(0, Math.min(0.5, p));
  return p * 100;
}

export const GalleryCardWrapper = ({
  tab,
  image,
  title,
  gallery,
  onClick,
  albumTitle,
  photo_art_gallery_view,
  cardType,
  /** Optional: force map styling regardless of URL */
  context,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();

  const [imageHasLoaded, setImageHasLoaded] = useState(false);
  const [photoIsFavorite, setPhotoIsFavorite] = useState(false);

  // PORTRAIT OFFSET state
  const [isPortrait, setIsPortrait] = useState(false);
  const [portraitYPercent, setPortraitYPercent] = useState(0); // 0..50

  // Prefer explicit prop; fall back to URL detection (client side)
  const isMapRoute =
    typeof window !== "undefined" &&
    window.location?.pathname?.toLowerCase?.().includes("/customer/map");
  const isMapContext = context === "map" || (context !== "default" && isMapRoute);

  const isEventLogo = cardType === "gallery-event";

  const desktopConditionToRenderCardWithNoImage = !image;
  const mobileConditionToRenderCardWithNoImage = !imageHasLoaded && isMobile;

  // PORTRAIT OFFSET: detect orientation + compute y%
  const onImageHasLoaded = (e) => {
    if (e?.target) {
      const w = e.target.naturalWidth || 0;
      const h = e.target.naturalHeight || 0;
      const portrait = h > w;
      setIsPortrait(portrait);
      if (portrait && !isEventLogo) {
        const y = computePortraitYPercent(h, w);
        setPortraitYPercent(y);
      } else {
        setPortraitYPercent(50); // center for non-portrait or logos
      }
      setImageHasLoaded(true);
    }
  };

  const handleKeyActivate = (e) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(e);
    }
  };

  const renderInnerLabel = () => (
    <Stack direction="row" className={classes.innerLabelWrapper}>
      <div
        className={classes.photoIsFavoriteWrapper}
        onClick={(e) => e.stopPropagation()}
      >
        {!photoIsFavorite && (
          <FavoriteBorderIcon onClick={() => setPhotoIsFavorite(true)} />
        )}
        {photoIsFavorite && (
          <FavoriteIcon
            className={classes.photoIsFavorite}
            onClick={() => setPhotoIsFavorite(false)}
          />
        )}
      </div>

      {gallery?.gallery_price && (
        <div className={classes.galleryPriceWrapper} onClick={(e) => e.stopPropagation()}>
          <p>
            from{" "}
            <CurrencyValue
              value={get(gallery, ["gallery_price", "base_price"])}
              country={gallery.gallery_price.country}
            />
          </p>
        </div>
      )}

      <div
        className={classes.shoppingCartButtonWrapper}
        onClick={(e) => e.stopPropagation()}
      >
        <AddToTrolleyButton
          variant="icon"
          gallery={gallery}
          pid={get(gallery, "first_gallery_photograph_ref")}
        />
      </div>
    </Stack>
  );

  const showNoImageCard =
    mobileConditionToRenderCardWithNoImage || desktopConditionToRenderCardWithNoImage;

  // Build title content
  const makeTitleContent = (titleString) => {
    if (!titleString) return null;
    if (isEventLogo) return <p className={classes.titleMain}>{titleString}</p>;
    const { dateTime, rest } = splitDateTimeAndName(titleString);
    if (dateTime || rest) {
      return (
        <>
          {dateTime && <p className={classes.dateLine}>{dateTime}</p>}
          {rest && <p className={classes.titleMain}>{rest}</p>}
          {!rest && !dateTime && <p className={classes.titleMain}>{titleString}</p>}
        </>
      );
    }
    return <p className={classes.titleMain}>{titleString}</p>;
  };

  const parsed = splitDateTimeAndName(title || "");
  const a11yLabel =
    (parsed.rest ? `Open ${parsed.rest}` : title ? `Open ${title}` : "") ||
    (albumTitle ? `Open ${albumTitle}` : "") ||
    "Open item";

  // PORTRAIT OFFSET: inline style applies the computed y% (x is always centered)
  const imgStyle =
    isPortrait && !isEventLogo
      ? { objectPosition: `50% ${portraitYPercent}%` }
      : undefined;

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyActivate}
      role="button"
      tabIndex={0}
      aria-label={a11yLabel}
      className={`${classes.card} ${isMapContext ? classes.cardMap : ""} cardtype-${
        cardType ?? "unknown"
      } ${tab === "events" ? classes.eventsCardWrapper : ""} ${
        tab === "photo_art_genre" ? classes.photo_art_card : ""
      } ${tab === "photo_art_belonging_to_genre" ? classes.photo_art_card : ""}`}
    >
      <div className={`${classes.cardInner} card-inner`}>
        {/* MEDIA AREA */}
        <div className={showNoImageCard ? classes.card_with_no_image : classes.mediaBox}>
          {!image && <p className={classes.coming_soon_text}>Coming Soon</p>}

          {photo_art_gallery_view && renderInnerLabel()}

          {image && !isMobile && (
            <img
              src={image}
              draggable="false"
              alt=""
              onLoad={onImageHasLoaded}
              className={classes.img}
              style={imgStyle}
              loading="lazy"
            />
          )}
          {image && isMobile && (
            <img
              src={image}
              draggable="false"
              onLoad={onImageHasLoaded}
              alt=""
              className={classes.img}
              style={imgStyle}
              loading="lazy"
            />
          )}
        </div>

        {/* TITLES BELOW IMAGE */}
        {tab !== "photo_art_belonging_to_genre" && (title || albumTitle) && (
          <div className={classes.titleRow}>
            {title && <div className={classes.titlePill}>{makeTitleContent(title)}</div>}
            {albumTitle && (
              <div className={classes.albumPill}>
                <p className={classes.pillText}>{albumTitle}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
